import { FocusStyleManager } from "@blueprintjs/core";
import React, { useEffect, useState } from "react";
import {
  Redirect,
  Route,
  RouteProps,
  Switch,
  BrowserRouter as Router,
  useHistory,
} from "react-router-dom";
import styled from "styled-components";
import {
  AuthStates,
  getAuthenticationState,
  getUserData,
  isAuthenticated,
} from "./screens/auth/auth";
import AppNavbar from "./components/AppNavbar";
import Tracking from "./components/Globals";
import {
  ORGANIZATION_KEY,
  OrgContext,
  PermissionsContext,
  UserContext,
} from "./contexts";
import GlobalStyles from "./GlobalStyles";
import { OrgData, UserData } from "./types";
import Footer from "./screens/home/components/Footer";
import * as Sentry from "@sentry/react";
import { AppCrashFallback } from "./components/ErrorBoundary";
import { recoverBrowserSecurityError } from "./utils/storage";
import SmartRedirect from "./screens/editor/SmartRedirect";
import ManageRedirect from "./screens/dashboard/ManageRedirect";
import GitLabSemgrepScreen from "./screens/GitLabSemgrepLanding";
import GlobalHelmet from "./GlobalHelmet";
import RobotsNoindex from "./components/RobotsNoindex";
import LearnHome from "./screens/learn/LearnHome";
import { getFeatureFlags } from "./screens/deployment/api";
import LoadingScreen from "./components/LoadingScreen";
import { HotkeysProvider } from "@blueprintjs/core";
import IssueSentimentResponseScreen from "./screens/IssueSentimentResponseScreen";
import { loadPermissions, Permission } from "./types/Permissions";
import { fetchUserPermissions } from "./api";
import { withToaster } from "./util";
import { RouteComponentProps } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";

export const USE_OLD_PLAYGROUND = "pleditor_opt_out";

const CheatsheetEmbed = React.lazy(
  () => import("./screens/embed/CheatsheetEmbed")
);
const EditorScreen = React.lazy(() => import("./screens/editor/EditorHome"));
const RuleWizardScreen = React.lazy(
  () => import("./screens/editor/wizard/RuleWizardScreen")
);
const LoginScreen = React.lazy(() => import("./screens/auth/LoginScreen"));
const GettingStartedScreen = React.lazy(
  () => import("./screens/GettingStartedScreen")
);

// Semgrep Cloud

const DashboardApp = React.lazy(
  () => import("./screens/dashboard/DashboardApp")
);

// End Semgrep Cloud

const UserActionScreen = React.lazy(() => import("./screens/UserActionScreen"));
const EditorWidget = React.lazy(
  () => import("./screens/editorWidget/EditorWidget")
);
const TosContent = React.lazy(() => import("./screens/tos/TosContent"));
const GithubInstallRedirect = React.lazy(
  () => import("./screens/auth/GithubInstallRedirect")
);
const GitlabOrganizationsScreen = React.lazy(
  () => import("./screens/GitlabOrganizations")
);
const OAuthCallbackScreen = React.lazy(
  () => import("./screens/auth/OAuthCallbackScreen")
);
const SamlFinishScreen = React.lazy(
  () => import("./screens/auth/SamlFinishScreen")
);
const LoginOAuthRedirect = React.lazy(
  () => import("./screens/auth/LoginOAuthRedirect")
);

const Admin = React.lazy(() => import("./screens/admin/Admin"));

const Learn = React.lazy(() => import("./screens/learn/Learn"));

const HomeScreen = React.lazy(() => import("./screens/home/HomeScreen"));

const AppLanding = React.lazy(() => import("./screens/appLanding/AppLanding"));

const PricingScreen = React.lazy(() => import("./screens/pricing/Pricing"));

const ContactUs = React.lazy(() => import("./screens/contactUs/ContactUs"));

const DeepSemgrep = React.lazy(() => import("./screens/contactUs/DeepSemgrep"));

const PlaygroundScreen = React.lazy(
  () => import("./screens/dashboard/workbench/Screen")
);

const ContactUsThankYou = React.lazy(
  () => import("./screens/contactUs/ContactUsThankYou")
);

const RegistryScreen = React.lazy(
  () => import("./screens/registry/RegistryScreen")
);

const ErrorScreen: React.FC = () => (
  <div style={{ padding: "80px", fontSize: "large" }}>
    Semgrep encountered an unrecoverable error. Please email support@r2c.dev and
    let us know so we can fix it!
  </div>
);

const SsoErrorScreen: React.FC<RouteComponentProps> = () => {
  const history = useHistory();
  const [providerId, setProviderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errDesc, setErrDesc] = useState<string | null>(null);
  useEffect(() => {
    const urlParams: URLSearchParams = new URLSearchParams(
      history.location.search
    );
    const qsProviderId = urlParams.get("providerId");
    const qsError = urlParams.get("error");
    const qsErrorDesc = urlParams.get("desc");
    setProviderId(qsProviderId);
    setError(qsError);
    setErrDesc(qsErrorDesc);
    Sentry.captureMessage(
      `CSE: User with SSO '${qsProviderId}' encountered error: '${qsError}' with description: '${qsErrorDesc}'`
    );
  }, [history.location.search]);

  return (
    <div style={{ padding: "80px", fontSize: "large" }}>
      <h1>SSO Error</h1>
      Semgrep encountered an SSO error{" "}
      <strong>
        <code>{error}</code>
      </strong>
      . Please email support@r2c.dev and let us know so we can fix it!
      <br />
      <br />
      <h3>ProviderId</h3>
      <pre>{providerId}</pre>
      <h3>Description</h3>
      <pre>{errDesc}</pre>
    </div>
  );
};

FocusStyleManager.onlyShowFocusOnTabs();

const AppContainer = styled.main`
  padding-top: var(--global-spacing-navbar-height);
`;

const RegistryAppContainer = styled(AppContainer)`
  background: #ffffff;
  min-height: 100vh;
`;

const PlaygroundContainer = styled(AppContainer)`
  height: 100vh;
  width: 100vw;
`;

const AuthedRoute: React.FC<RouteProps> = (props) => {
  switch (getAuthenticationState()) {
    case AuthStates.Unauthorized:
      return (
        <Redirect
          push
          to={{
            pathname: "/login",
            search: `?return_path=${window.location.pathname}`,
          }}
        />
      );
    case AuthStates.Authorized:
      return (
        <>
          <RobotsNoindex />
          <Route {...props} />
        </>
      );
    default:
      return <div>Error.</div>;
  }
};

const UserActionPrompts: React.FC = () => (
  <AppContainer>
    <AppNavbar />
    <Switch>
      <Route path="/terms" exact>
        <TosContent />
      </Route>
      <Route path="/">
        <UserActionScreen />
      </Route>
    </Switch>
  </AppContainer>
);

/**
 * temporary router for the playground links
 * determines if user goes to old or new playground
 */
const PlaygroundRouter: React.FC = () => {
  const history = useHistory();
  const params = new URLSearchParams(history.location.search);
  const currentPath = history.location.pathname;

  // for redirecting from old to new playground - registry and blank rules
  const ruleIdFromParams = params.get("registry");
  const newPlaygroundRegistryUrl =
    ruleIdFromParams === null ? "new" : `r/${ruleIdFromParams}`;

  // for redirecting from old to new playground - snippets
  const newPlaygroundSnippetUrl = `/playground${currentPath}`;

  // for redirecting from new to old playground
  let oldPlaygroundUrl = "";
  if (currentPath.startsWith("/playground/r")) {
    const registryRuleId = currentPath.slice("/playground/r/".length);
    oldPlaygroundUrl = `/playground?registry=${registryRuleId}`;
  } else if (currentPath.startsWith("/playground/s")) {
    const ruleId = currentPath.slice("/playground/s/".length);
    oldPlaygroundUrl = `/s/${ruleId}`;
  } else {
    // blank rule
    oldPlaygroundUrl = "/playground";
  }

  return (
    <Switch>
      <Route
        path="/playground"
        exact
        render={() =>
          recoverBrowserSecurityError(() =>
            localStorage.getItem(USE_OLD_PLAYGROUND)
          ) !== null ? (
            <AppContainer>
              <AppNavbar />
              <EditorScreen />
              <Footer />
            </AppContainer>
          ) : (
            <Redirect to={`/playground/${newPlaygroundRegistryUrl}`} />
          )
        }
      />
      <Route
        path={`/playground/:bundleAddress*`}
        exact
        render={() =>
          recoverBrowserSecurityError(() =>
            localStorage.getItem(USE_OLD_PLAYGROUND)
          ) !== null ? (
            <Redirect to={oldPlaygroundUrl} />
          ) : (
            <>
              <AppNavbar />
              <PlaygroundContainer>
                <PlaygroundScreen />
              </PlaygroundContainer>
            </>
          )
        }
      />
      <Route
        path="/s/:snippetFqname"
        exact
        render={() =>
          recoverBrowserSecurityError(() =>
            localStorage.getItem(USE_OLD_PLAYGROUND)
          ) !== null ? (
            <AppContainer>
              <AppNavbar />
              <EditorScreen />
            </AppContainer>
          ) : (
            <Redirect to={newPlaygroundSnippetUrl} />
          )
        }
      />
    </Switch>
  );
};

const NormalApp: React.FC<{ user?: UserData; org?: OrgData }> = (props) => {
  const queryString = window.location.search;
  return (
    <Switch>
      <Route path="/" exact>
        <AppNavbar />
        <HomeScreen />
      </Route>
      <Route path="/login" exact>
        <AppNavbar showLogin={false} />
        <LoginScreen />
      </Route>
      <Route path="/products/semgrep-app" exact>
        <AppNavbar />
        <AppLanding />
      </Route>
      <Route path="/pricing" exact>
        <AppContainer>
          <AppNavbar />
          <PricingScreen />
        </AppContainer>
      </Route>
      <Route path="/getting-started" exact>
        <AppNavbar />
        <GettingStartedScreen />
      </Route>
      <Route path="/contact" exact>
        <AppNavbar />
        <ContactUs />
      </Route>
      <Route path="/deep-semgrep-beta" exact>
        <AppNavbar />
        <DeepSemgrep />
      </Route>
      <Route path="/contact/thank-you" exact>
        <AppNavbar />
        <ContactUsThankYou />
      </Route>
      <Route path="/for/gitlab" exact>
        <AppNavbar />
        <GitLabSemgrepScreen />
      </Route>
      <Route
        path="/oauth/login/:authProviderId"
        component={LoginOAuthRedirect}
        exact
      />
      <Route
        path="/oauth/callback/:authProviderId"
        component={OAuthCallbackScreen}
        exact
      />
      <Route path="/auth/saml/finish" component={SamlFinishScreen} exact />
      <Route path="/github/login" exact>
        <Redirect to="/oauth/login/github" />
      </Route>
      <Route path="/github/install" exact>
        <GithubInstallRedirect />
      </Route>
      <Route path="/gitlab/organizations" exact>
        <GitlabOrganizationsScreen />
      </Route>
      <Route path="/callback" exact>
        <Redirect to="/oauth/callback/github" />
      </Route>
      <Route path="/embed/cheatsheet" exact component={CheatsheetEmbed} />
      <Route path="/embed/editor" exact component={EditorWidget} />
      <Route path="/registry">
        <Redirect to="/r" />
      </Route>
      <Redirect from="/packs" to="/r" exact />
      <Redirect from="/rulesets" to="/r" exact />
      <Route path="/r" exact>
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="rules" />
        </RegistryAppContainer>
      </Route>
      <Route path="/r/:registryId">
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="rules" />
        </RegistryAppContainer>
      </Route>
      <Route path="/explore" exact>
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="explore" />
        </RegistryAppContainer>
      </Route>
      <Route path="/p/:packId" exact>
        <RegistryAppContainer>
          <AppNavbar />
          <RegistryScreen defaultTabId="one-pack" />
        </RegistryAppContainer>
      </Route>
      <AuthedRoute path="/orgs/:orgName" component={DashboardApp} />
      <Route path="/issue-sentiment-response" exact>
        <AppNavbar />
        <IssueSentimentResponseScreen />
      </Route>
      <Route path={["/playground", "/s"]}>
        <PlaygroundRouter />
      </Route>
      <Route path="/:path?">
        <AppContainer>
          <AppNavbar />
          <Switch>
            <AuthedRoute path="/manage/:sections*" component={ManageRedirect} />
            {props.user?.roles?.admin === true ? (
              <Route path="/admin" exact component={Admin} />
            ) : null}
            <Route path="/fatal-error" component={ErrorScreen} exact />
            <Route path="/sso-error" component={SsoErrorScreen} exact />
            <Redirect from="/editor" to={`/playground${queryString}`} exact />
            <Route path="/wizard" exact component={RuleWizardScreen} />
            <Route path="/learn" exact component={LearnHome} />
            {/* Legacy support for links to e.g. /learn/3 */}
            <Route path="/learn/:step" exact component={LearnHome} />
            <Route path="/learn/:tutorial/:step" component={Learn} />
            <Route path="/terms" exact>
              <TosContent />
            </Route>
            <Route path="/:snippetFqname" component={SmartRedirect} exact />
          </Switch>
          <Footer />
        </AppContainer>
      </Route>
    </Switch>
  );
};

const App: React.FC<{}> = () => {
  const [org, setOrg] = useState<OrgData>();
  // orgFeaturesLoaded defaults to true so that when a user is not signed in,
  // (and org never needs loading), we do not show spinner
  const [orgFeaturesLoaded, setOrgFeaturesLoaded] = useState<boolean>(true);
  const history = useHistory();
  const user = isAuthenticated() ? getUserData() : undefined;
  const username = user?.username;
  /* Sort orgs from most recent to least recent */
  const firstOrg = user?.orgs.sort(
    (a: OrgData, b: OrgData) => parseInt(b.id) - parseInt(a.id)
  )[0];

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(true);
  const path = history.location.pathname;
  const url_org_name =
    path.startsWith("/orgs/") && path.split("/").length > 2
      ? path.split("/")[2]
      : undefined;

  useEffect(() => {
    // Load user and org from local storage at start-up
    if (!username || org) return;
    setOrgFeaturesLoaded(false);
    const savedOrgData = recoverBrowserSecurityError(() =>
      localStorage.getItem(ORGANIZATION_KEY)
    );
    let savedOrg: OrgData | undefined = undefined;
    let newOrg: OrgData | undefined;
    if (savedOrgData) {
      savedOrg = JSON.parse(savedOrgData);
    }
    if (url_org_name && user?.orgs.some((o) => o.name === url_org_name)) {
      // If URL specifies an org, load that org
      newOrg = user?.orgs.find((o) => o.name === url_org_name);
    } else if (user?.orgs.some((o) => o.id === savedOrg?.id || 0)) {
      // If URL does not successfully load an org, use saved org data
      newOrg = savedOrg;
    } else if (firstOrg) {
      // In absence of saved org, use most-recently-added org
      newOrg = firstOrg;
    }

    // load feature flags for org from backend
    if (newOrg) {
      syncOrgFeatureFlags(newOrg);
      // do not call setOrgFeaturesLoaded in this case until promise resolves
      // (it is done in the finally clause of syncOrg)
    } else {
      setOrgFeaturesLoaded(true);
    }
    // eslint-disable-next-line
  }, []); // This must remain empty so the useEffect only runs once.

  useEffect(() => {
    // Load permissions every time org or user is changed
    if (!user || !org || permissionsLoaded) return;

    withToaster(
      () =>
        fetchUserPermissions(org.id)
          .then((response) =>
            setPermissions(loadPermissions(response.authorized_actions))
          )
          .finally(() => setPermissionsLoaded(true)),
      (err) =>
        `Error loading permissions for deployment '${org.id}': ${err.message}`
    )();
  }, [org, user, permissionsLoaded]);

  const syncOrgFeatureFlags = (org: OrgData) => {
    // load feature flags for org from backend
    setOrgFeaturesLoaded(false);
    getFeatureFlags(org.id)
      .then((resp) => {
        org!.features = resp.features;
        org!.has_rbac = resp.has_rbac;
        setOrg(org);
        setPermissionsLoaded(false);
        recoverBrowserSecurityError(() =>
          localStorage.setItem(ORGANIZATION_KEY, JSON.stringify(org))
        );
      })
      .finally(() => setOrgFeaturesLoaded(true));
  };

  const renderOrg = user ? org : undefined;

  const needsUserAction =
    user && (!user.email || user.flags?.has_filled_survey === undefined);

  // permissions must be loaded to prevent 404 from flashing on load
  // while other routes are blocked behind permissions
  if (!orgFeaturesLoaded || !permissionsLoaded) return <LoadingScreen />;

  return (
    <UserContext.Provider value={{ user }}>
      <OrgContext.Provider
        value={{
          org: renderOrg,
          setOrg: syncOrgFeatureFlags,
        }}
      >
        <PermissionsContext.Provider
          value={{
            permissions: permissions,
          }}
        >
          <Route path="/">
            <Tracking />
          </Route>
          <React.Suspense fallback={<LoadingScreen />}>
            {needsUserAction ? (
              <UserActionPrompts />
            ) : (
              <NormalApp user={user} org={renderOrg} />
            )}
          </React.Suspense>
        </PermissionsContext.Provider>
      </OrgContext.Provider>
    </UserContext.Provider>
  );
};

/**
 * Defaults:
 *  refetchOnWindowFocus set to false. Default is true, resulting in a lot of extra queries
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * We add the react-router outside the App element, so that calling
 * history.go(0) re-renders the whole <App />.
 */
const RoutedApp = () => (
  <HotkeysProvider>
    <QueryClientProvider client={queryClient}>
      <Router>
        <GlobalStyles />
        <GlobalHelmet />
        <App />
      </Router>
    </QueryClientProvider>
  </HotkeysProvider>
);

export default Sentry.withErrorBoundary(RoutedApp, {
  showDialog: true,
  fallback: ({ resetError }) => <AppCrashFallback resetError={resetError} />,
});
